/**
 * WR Beautiful — Recently Viewed Products Web Component
 * Stores product handles in localStorage, renders on subsequent page loads.
 */
if (!customElements.get('wr-recently-viewed')) {
  class WrRecentlyViewed extends HTMLElement {
    constructor() {
      super();
      this.STORAGE_KEY = 'wr_recently_viewed';
      this.MAX_ITEMS = 8;
    }

    connectedCallback() {
      /* v13 — robust current-handle detection: prefer data attribute,
         fall back to extracting handle from the page URL (covers edge
         cases where the Liquid `product` object isn't available in the
         section rendering context). */
      let currentHandle = this.dataset.currentHandle;
      if (!currentHandle) {
        const pathMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
        if (pathMatch) currentHandle = pathMatch[1];
      }
      const sectionId = this.dataset.sectionId;

      /* Record current product */
      if (currentHandle) {
        this._addHandle(currentHandle);
      }

      /* Render previously viewed products */
      this._render(currentHandle, sectionId);
    }

    _getHandles() {
      try {
        const raw = localStorage.getItem(this.STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
      } catch {
        return [];
      }
    }

    _addHandle(handle) {
      let handles = this._getHandles();
      /* Remove if already in list (will re-add at front) */
      handles = handles.filter((h) => h !== handle);
      handles.unshift(handle);
      /* Limit to MAX_ITEMS */
      if (handles.length > this.MAX_ITEMS) {
        handles = handles.slice(0, this.MAX_ITEMS);
      }
      try {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(handles));
      } catch {
        /* localStorage full — silently fail */
      }
    }

    async _render(currentHandle, sectionId) {
      let handles = this._getHandles();
      /* Exclude current product */
      handles = handles.filter((h) => h !== currentHandle);

      /* Hide section when fewer than 2 items to display.
         A single lonely product card looks odd; threshold of 2
         ensures the grid has meaningful content.
         Also set aria-hidden for screen readers and clear grid to
         prevent flash-of-content on slow connections. */
      if (handles.length < 2) {
        this.style.display = 'none';
        this.setAttribute('aria-hidden', 'true');
        const grid = this.querySelector('.wr-recently-viewed__grid');
        if (grid) grid.innerHTML = '';
        return;
      }

      /* Show the container */
      this.style.display = '';
      this.removeAttribute('aria-hidden');

      const grid = this.querySelector('.wr-recently-viewed__grid');
      if (!grid) return;

      /* Fetch product cards via section rendering API */
      const limit = Math.min(handles.length, 4);
      const displayHandles = handles.slice(0, limit);

      /* Fetch each product's card HTML via the product URL + section rendering */
      const fetchPromises = displayHandles.map((handle) =>
        fetch(`/products/${handle}?section_id=${sectionId}`)
          .then((res) => {
            if (!res.ok) throw new Error(res.status);
            return res.text();
          })
          .then((html) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const card = doc.querySelector('.wr-recently-viewed__card-slot');
            return card ? card.innerHTML : null;
          })
          .catch(() => null)
      );

      const results = await Promise.all(fetchPromises);
      const validCards = results.filter(Boolean);

      /* Also enforce minimum after fetch (some handles may fail to load) */
      if (validCards.length < 2) {
        this.style.display = 'none';
        return;
      }

      grid.innerHTML = validCards
        .map((cardHtml) => `<div role="listitem">${cardHtml}</div>`)
        .join('');
    }
  }

  customElements.define('wr-recently-viewed', WrRecentlyViewed);
}
